// BUILD-046 task 8.10: the simulated-identity banner (Req 2.5). Rendered at the top of every My Work view. It states that the Caller
// identity is simulated and is not authentication, it is visible without any action, and it has no close control and no state that
// could hide it. The pilot tools panel sits inside it and renders only in the local pilot (LOCAL_PILOT) and only when `me.simulated` is true.
//
// Correction cycle 1bb88b3e (stage B2a, item N9): ONE compact, unmistakable "SIMULATED preview" notice. It holds, on one line that wraps on a narrow
// screen: the notice, the single persona control ("Simulated caller"; it used to sit in the page header and overlapped the navigation at 1280 and 1024 px),
// and a "Pilot tools" disclosure whose body holds Reset (closed by default, with a confirmation step). Nothing in it can hide the notice.
import type { WorkMe } from '../../api/workShared';
import { PilotCallerSwitcher } from '../../components/sharedTasks/PilotCallerSwitcher';
import { bannerModel } from './identityModel';
import { LOCAL_PILOT } from './localPilot';
import { PilotToolsPanel } from './PilotToolsPanel';
import { PopoverButton } from './DropdownMenu';

/** Between "Simulated preview" and "Test data": a middle dot (U+00B7) with a space on each side. */
const BANNER_SEPARATOR = ' \u00b7 ';

export interface SimulatedIdentityBannerProps {
  /** The result of `GET /work/me`, or null while it has not loaded (or failed). */
  me: Pick<WorkMe, 'simulated' | 'identity' | 'employee'> | null;
  /** True when the page runs against the local pilot (local mock auth), so the label shows even if `/work/me` failed. */
  localPilot?: boolean;
  onPilotReset?: () => void;
  /** Full width under the page header and sticky (My Work). Without it the notice is an inline bar inside the page's own container (Work groups). */
  bleed?: boolean;
}

export function SimulatedIdentityBanner({ me, localPilot = false, onPilotReset, bleed = false }: SimulatedIdentityBannerProps) {
  const view = bannerModel(me, { localPilot });
  if (!view.visible) return null;
  // Layout-6: ONE slim pale-amber strip. Left: the explicit notice "Simulated preview · Test data" (D1 display cleanup, Owner direction: the one banner says it once, so
  // the labels below it carry no "(FICTIONAL)" suffix; it replaces "SIMULATED PREVIEW • Fictional data"). Right: "Viewing as <persona> ▾" and, after a thin divider, "Pilot tools ▾". It wraps to two lines on a
  // narrow screen and, with `bleed`, sits full width under the HQ header and stays in view while the page scrolls (nothing in it can hide the notice).
  return (
    <div
      role="note"
      aria-label={`Simulated preview notice. ${view.text}`}
      title={view.text}
      className={bleed ? 'sticky top-0 z-40 border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-xs text-amber-900 lg:px-6' : 'mb-3 rounded border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-900'}
      data-testid="simulated-identity-banner"
    >
      <div className={`flex flex-wrap items-center justify-between gap-x-4 gap-y-1 ${bleed ? 'mx-auto max-w-[69rem]' : ''}`}>
        <p className="flex items-center gap-2 font-semibold">
          <span aria-hidden="true" className="h-2 w-2 flex-shrink-0 rounded-full bg-amber-500" />
          {/* D1 display cleanup: the ONE banner of the page. Its text is one string, "Simulated preview · Test data" (a middle dot, U+00B7, with spaces); the
              "(FICTIONAL)" suffix is no longer repeated on every name, job and title below it. */}
          <span data-testid="simulated-banner-text">
            Simulated preview{BANNER_SEPARATOR}
            <span className="font-normal">Test data</span>
          </span>
          <span className="sr-only">. The identity is simulated, not authentication.</span>
        </p>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {/* The one persona control (local pilot only). Outside the pilot the name is shown as text. */}
          {LOCAL_PILOT ? <PilotCallerSwitcher variant="strip" /> : view.callerName !== null && <span>Viewing as {view.callerName}</span>}
          {/* The pilot tools exist only in the local pilot: LOCAL_PILOT is statically false in any `vite build`, so a shipped bundle carries no
              Reset control or text (task 8.21 bundle check). Within the pilot the panel still shows only when the server reports a simulated identity. */}
          {LOCAL_PILOT && view.showPilotTools && (
            <>
              <span aria-hidden="true" className="h-4 w-px bg-amber-300" />
              <PopoverButton
                testId="pilot-tools-button"
                buttonText="Pilot tools"
                ariaLabel="Pilot tools"
                panelLabel="Pilot tools"
                align="right"
                keepMounted
                panelClassName="w-80 max-w-[calc(100vw-2rem)]"
                buttonClassName="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-amber-900 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
              >
                <PilotToolsPanel simulated={view.showPilotTools} open onReset={onPilotReset} layout="menu" />
              </PopoverButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
